// Pure helpers for broadcast stats + lifecycle, extracted so they're unit-tested
// without rendering. Used by ViewBroadcast (real numbers, replacing the old
// hardcoded demo values) and the Broadcasts list (status chip).

// Format a 0..1 ratio as a percentage string, guarding against NaN/Infinity
// (e.g. a rate with a zero denominator).
export function valueToPercent(value) {
  if (value == null || !isFinite(value) || isNaN(value)) {
    return "0.00%";
  }
  return (100 * value).toFixed(2) + "%";
}

// pickWinningVariantId returns the id of the leading A/B variant — the active
// variant with the highest open rate (opens/sent) among those that have sent at
// least one email. Returns null when there's no meaningful winner: fewer than two
// active variants with sends, so a "winner" badge would be misleading.
export function pickWinningVariantId(mailVariants) {
  const contenders = (mailVariants || []).filter((v) => v.should_run !== false && (v.sent || 0) > 0);
  if (contenders.length < 2) {
    return null;
  }
  let best = null;
  let bestRate = -1;
  for (const v of contenders) {
    const rate = (v.opens || 0) / v.sent;
    if (rate > bestRate) {
      bestRate = rate;
      best = v;
    }
  }
  return best ? best.id : null;
}

// Per-variant open/click rates for the A/B comparison cards, as percentage
// strings ("—" when nothing has sent yet so we never divide by zero).
export function variantRates(item) {
  const sent = (item && item.sent) || 0;
  const pct = (n) => (sent > 0 ? Math.round(((n || 0) / sent) * 100) + "%" : "—");
  return {
    openRate: pct(item && item.opens),
    clickRate: pct(item && item.clicks),
  };
}

// Roll the broadcast record + its mail variants into the numbers the
// performance panel shows. Broadcast-level counters (sent/opened/bounces/
// complaints) are authoritative; per-variant counters (unsubscribes/conversions)
// are summed across variants.
export function computeBroadcastStats(record, mailVariants) {
  const sent = (record && record.sent) || 0;
  const opened = (record && record.opened) || 0;
  const bounces = (record && record.bounces) || 0;
  const complaints = (record && record.complaints) || 0;

  const variants = mailVariants || [];
  const unsubscribes = variants.reduce((sum, v) => sum + ((v && v.unsubscribes) || 0), 0);
  const conversions = variants.reduce((sum, v) => sum + ((v && v.conversions) || 0), 0);

  return {
    sent,
    opened,
    bounces,
    complaints,
    unsubscribes,
    conversions,
    openRate: sent > 0 ? opened / sent : 0,
    conversionRate: sent > 0 ? conversions / sent : 0,
    bounceRate: sent > 0 ? bounces / sent : 0,
  };
}

// Display metadata for a broadcast lifecycle status (mirrors the backend
// BroadcastStatus* constants). Color names map to Arco Tag colors.
export function broadcastStatusMeta(status) {
  switch (status) {
    case "sending":
      return { label: "Sending", color: "arcoblue" };
    case "sent":
      return { label: "Sent", color: "green" };
    case "scheduled":
      return { label: "Scheduled", color: "gold" };
    case "failed":
      return { label: "Failed", color: "red" };
    case "draft":
    default:
      return { label: "Draft", color: "gray" };
  }
}

// A broadcast can be sent only from draft/scheduled and only once a sender +
// contact list are chosen. (The backend re-validates — incl. that a verified
// sender and an active mail variant exist — and returns a specific 400 message,
// which the UI surfaces.)
export function canSendBroadcast(record) {
  if (!record) {
    return false;
  }
  const status = record.status || "draft";
  const sendable = status === "draft" || status === "scheduled";
  return Boolean(sendable && record.sender_email_id && record.contact_list_id);
}

// A date the server never set arrives as year 1, not as nothing, so it counts
// as no date.
export function realDate(value) {
  const at = Date.parse(value);
  return !isNaN(at) && new Date(at).getUTCFullYear() > 1 ? value : null;
}
